/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { model, repository } from '@whizz/api-client';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import type NewStudentBasic from '@whizz/api-client/dist/@types/model/tx3/NewStudentBasic';
import type { default as LessonHistoryClient } from '@whizz/api-client/dist/@types/model/tx3/LessonHistory';
import type { StudentType } from '../types';
import { useMixpanel } from 'react-mixpanel-browser';
import { GlobalDashboardLayout } from '../../layouts';
import { LessonHistoryTable, Settings } from './modules';
import { useClassSelector } from '../../hooks/filters/useClassSelector';
import { useDateRangeSelector } from '../../hooks/filters/useDateRangeSelector';
import { mapGroupBasicStudentsObjects } from '../../mapper/groupMapper';
import { Alert } from '../../components';
import { getDatesParams } from '../../helper/getDatesParams';
import { QUERIES_IDS } from '../../util/vars';
import type { DateTime } from 'luxon';
import { useQuery } from 'react-query';
import { dateTimeToDateString } from '../../util/dates';
import { StudentSearch } from '../../global';
import type { OptionTypeBase } from 'react-select';
import NoClasses from '../../global/sections/NoClasses';

const LessonHistory: FunctionComponent = () => {
  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const teacherId = (me as Teacher).getId();
  const mixpanel = useMixpanel();

  const [ searchBoxValue, setSearchBoxValue ] = useState<OptionTypeBase | null>(null);
  const [ dateRangeSelector, dateValue, customDateValue ] = useDateRangeSelector({ maxDate: new Date() });
  const [ academicYear, setAcademicYear ] = useState<DateTime | null>(null);
  const [ showNoClasses, setShowNoClasses ] = useState(false);

  const classesQuery = useQuery(
    [ QUERIES_IDS.CLASSES, teacherId ],
    () => repository.NewTeacherRepository.getClasses(teacherId!),
    {
      enabled: null != teacherId,
      placeholderData: [],
      onSuccess: (data) => {
        // TODO: Implement repository and interfaces
        if (data.length) model.School.academicYearStartDate(data[0].schoolId).then((date) => {
          setAcademicYear(date);
        });
      },
    },
  );

  const [ classSelector, classValue ] = useClassSelector(me?.username as string, classesQuery.data!, 'space-between');

  const mapQueryData = useCallback((newStudents: NewStudentBasic[]) => mapGroupBasicStudentsObjects(newStudents), []);

  const studentQuery = useQuery<NewStudentBasic[], unknown, StudentType[]>(
    [ QUERIES_IDS.GROUP_STUDENTS, classValue?.value ],
    // TODO: Implement repository and interfaces
    () => model.Group.getStudentsById(classValue!.value),
    {
      enabled: null != classValue,
      placeholderData: [],
      select: mapQueryData,
    },
  );

  const studentQueryData = studentQuery.data ?? [];

  const { startDateParam, endDateParam } = getDatesParams(
    dateValue?.value ?? 'last_week',
    customDateValue,
    academicYear,
    me,
  );

  const lessonHistoryQuery = useQuery<LessonHistoryClient[]>(
    [
      QUERIES_IDS.LESSON_HISTORY,
      searchBoxValue?.value,
      classValue?.value,
      dateTimeToDateString(startDateParam),
      dateTimeToDateString(endDateParam),
    ],
    // TODO: Implement repository and interfaces
    () => model.LessonHistory.manyByStudentId(
      parseInt(searchBoxValue!.value),
      classValue!.value,
      locale?.id,
      startDateParam,
      endDateParam ? endDateParam : undefined,
    ),
    {
      enabled: null != classValue && null != searchBoxValue && null != dateValue && null != academicYear,
      placeholderData: [],
    },
  );

  const isLoading = null != classValue
    && null != searchBoxValue
    && (
      !lessonHistoryQuery.isFetched
      || lessonHistoryQuery.isFetching
    );

  const content = () => {
    if (null == classValue) {
      return (
        <Alert show>
          {translate('pages.HISTORY.SELECT_STUDENT')}
        </Alert>
      );
    }

    if (null == searchBoxValue) {
      return (
        <Alert show>
          {translate('pages.HISTORY.SELECT_STUDENT')}
        </Alert>
      );
    }

    if (!lessonHistoryQuery.isFetched || lessonHistoryQuery.isFetching) {
      // The loader will overwrite it
      return null;
    }

    return (
      <LessonHistoryTable history={lessonHistoryQuery.data ?? []} />
    );
  };

  const searchBox =
    <StudentSearch
      students={studentQueryData}
      value={searchBoxValue}
      handleChange={(option) => {
        setSearchBoxValue(option);
      }}
      isFetchingData={!studentQuery.isFetched || studentQuery.isFetching}
      className={'w-full'}
      data-cy="student-search"
    />;

  useEffect(() => {
    setSearchBoxValue(null);
  }, [ classValue ]);

  useEffect(() => {
    if (classesQuery.isFetched && 0 === classesQuery.data?.length) {
      setShowNoClasses(true);
    }
  }, [ classesQuery ]);

  useEffect(() => {
    if (!mixpanel || !mixpanel?.config?.token) {
      return;
    }
    mixpanel.track('Lesson History');
  }, [ mixpanel ]);

  return (
    <GlobalDashboardLayout
      isLoading={isLoading}
      disableContainer
      title={translate('pages.HISTORY.HEADER')}
      actionContent={classSelector}
      classId={classValue?.value}
      settings={(
        <Settings
          searchStudent={searchBox}
          dateRangeSelector={dateRangeSelector}
        />
      )}
    >
      <div className={'mb-lg'}>
        {
          showNoClasses ?
            <NoClasses />
            : content()
        }
      </div>
    </GlobalDashboardLayout>
  );
};

export default LessonHistory;
